import { useCallback, useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import DateRangeFilter from '@/components/businessValue/DateRangeFilter';
import TicketTypeSettingsPanel from '@/components/businessValue/TicketTypeSettingsPanel';
import LlmPricingSettingsPanel from '@/components/businessValue/LlmPricingSettingsPanel';
import ModelPricingSettingsPanel from '@/components/businessValue/ModelPricingSettingsPanel';
import TokenUsageCharts from '@/components/businessValue/TokenUsageCharts';
import UsageDrillDownTable from '@/components/businessValue/UsageDrillDownTable';
import SavingsCharts from '@/components/businessValue/SavingsCharts';
import BusinessValueHero from '@/components/businessValue/BusinessValueHero';
import BusinessValueMetrics from '@/components/businessValue/BusinessValueMetrics';
import SavingsComparisonTable from '@/components/businessValue/SavingsComparisonTable';
import CostBreakdownDonut from '@/components/businessValue/CostBreakdownDonut';
import CumulativeSavingsChart from '@/components/businessValue/CumulativeSavingsChart';
import {
  fetchSavings,
  fetchTicketTypeSettings,
  fetchTokenUsage,
  fetchUsageDrillDown,
} from '@/lib/businessValueApi';
import { filterBusinessValueSettings } from '@/lib/businessValueCategories';

const defaultRange = { preset: 'last_7_days', fromDate: '', toDate: '' };

export default function BusinessValue() {
  const [range, setRange] = useState(defaultRange);
  const [activeTab, setActiveTab] = useState('overview');
  const [settings, setSettings] = useState([]);
  const [tokenUsage, setTokenUsage] = useState(null);
  const [usageDrillDown, setUsageDrillDown] = useState(null);
  const [savings, setSavings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const loadSettings = useCallback(async () => {
    const data = await fetchTicketTypeSettings();
    setSettings(filterBusinessValueSettings(data));
  }, []);

  const loadDashboard = useCallback(async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const [usage, drillDown, save] = await Promise.all([
        fetchTokenUsage(range),
        fetchUsageDrillDown(range),
        fetchSavings(range),
      ]);
      setTokenUsage(usage);
      setUsageDrillDown(drillDown);
      setSavings(save);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || err.message || 'Failed to load business value data');
    } finally {
      if (silent) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, [range]);

  const handleSettingsSaved = useCallback(async () => {
    await loadSettings();
    await loadDashboard({ silent: true });
  }, [loadSettings, loadDashboard]);

  useEffect(() => {
    loadSettings().catch(console.error);
  }, [loadSettings]);

  useEffect(() => {
    if (range.preset === 'custom' && (!range.fromDate || !range.toDate)) {
      return;
    }
    loadDashboard();
  }, [loadDashboard, range]);

  return (
    <div className="space-y-6" data-testid="business-value-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Business Value</h1>
        <p className="text-muted-foreground mt-2">
          Compare manual resolution cost to Halo LLM cost for ABAP, HTML, security, and NUP workflow tickets.
        </p>
      </div>

      <DateRangeFilter range={range} onChange={setRange} />

      {!loading && tokenUsage?.from_date && (
        <p className="text-sm text-muted-foreground -mt-3" data-testid="bv-active-period">
          Period {tokenUsage.from_date} → {tokenUsage.to_date}
          {savings?.ticket_count != null && (
            <>
              {' '}
              · {savings.ticket_count} BV ticket type{savings.ticket_count === 1 ? '' : 's'} in
              this period (security, code report, static report, workflow)
              {savings.tickets_with_llm_usage != null && savings.tickets_with_llm_usage > 0 && (
                <>
                  {' '}
                  · {savings.tickets_with_llm_usage} with orchestrator LLM usage recorded
                </>
              )}
              {tokenUsage.total_deploy_count > 0 && (
                <>
                  {' '}
                  · {tokenUsage.total_deploy_count} deployment
                  {tokenUsage.total_deploy_count === 1 ? '' : 's'}
                </>
              )}
            </>
          )}
          <span className="block text-xs mt-1">
            Dashboard ticket totals include all ticket types and are not limited to this date range.
          </span>
        </p>
      )}

      {error && (
        <div className="rounded-md border border-cp-line bg-cp-inset px-4 py-3 text-sm text-cp-crit">
          {typeof error === 'string' ? error : JSON.stringify(error)}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-48 text-muted-foreground">Loading...</div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="llm-cost">LLM Cost</TabsTrigger>
            <TabsTrigger value="usage-detail">By ticket / session</TabsTrigger>
            <TabsTrigger value="savings">Savings</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>
          {refreshing && (
            <p className="text-xs text-muted-foreground mt-2" data-testid="bv-refreshing">
              Refreshing totals…
            </p>
          )}

          <TabsContent value="overview" className="space-y-4 mt-4">
            <BusinessValueHero savings={savings} tokenUsage={tokenUsage} />
            <BusinessValueMetrics savings={savings} tokenUsage={tokenUsage} />
            <SavingsComparisonTable savings={savings} />
            <div className="grid gap-4 lg:grid-cols-[1fr_1.8fr]">
              <CostBreakdownDonut savings={savings} tokenUsage={tokenUsage} />
              <CumulativeSavingsChart savings={savings} />
            </div>
            <TokenUsageCharts data={tokenUsage} />
            <UsageDrillDownTable data={usageDrillDown} />
          </TabsContent>

          <TabsContent value="llm-cost" className="space-y-4 mt-4">
            <TokenUsageCharts data={tokenUsage} />
            <UsageDrillDownTable data={usageDrillDown} />
          </TabsContent>

          <TabsContent value="usage-detail" className="mt-4">
            <UsageDrillDownTable data={usageDrillDown} />
          </TabsContent>

          <TabsContent value="savings" className="mt-4">
            <SavingsCharts data={savings} />
          </TabsContent>

          <TabsContent value="settings" className="space-y-4 mt-4">
            <LlmPricingSettingsPanel onSaved={handleSettingsSaved} />
            <ModelPricingSettingsPanel onSaved={handleSettingsSaved} />
            <TicketTypeSettingsPanel settings={settings} onSaved={handleSettingsSaved} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
